import { Order } from '@commercelayer/sdk';
import { SiteLocale } from '@graphql/generated';
import { useAppContext } from '@lib/context/appContext';
import t, { tt } from '@lib/locales';
import { getFormattedAvailabilityText } from '@lib/utils/clUtils';
import { ProductAndBundleDataType } from '@model/product';
import { checkIfPossibleAddToCart, getCartQuantityforProduct } from '@utils/cart-utils';
import { getShippingMessage } from '@utils/shipping-message';
import { useEffect, useState } from 'react';
import config from '../../../data/config.json';

type Props = {
  locale: SiteLocale;
  product: ProductAndBundleDataType;
  addToCart: (product: ProductAndBundleDataType, quantity?: number) => void;
};

export const ProductItem = ({ locale, product, addToCart }: Props) => {
  const { availabilityShowThreshold, maxPromptDeliveryHours } = config.configuration;
  const daysPromptDeliveryLimit = maxPromptDeliveryHours / 24;
  const { levels = null, maxQuantity = 1 } = product;

  const [quantity, setQuantity] = useState(1);
  const [maxQty, setMaxQty] = useState(maxQuantity);
  const { state } = useAppContext();
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (product.metadata && product.metadata.max_cart_qty > 0) {
      product.maxQuantity = Math.min(product.metadata.max_cart_qty, maxQuantity);
    }
    setMaximumItemForCounter(state.order, product);
  }, [state.order]);

  useEffect(() => {
    async function fetchMessage() {
      const message = await getShippingMessage(locale);
      setMessage(message);
    }
    fetchMessage();
  }, []);

  const incQuantity = () => {
    setQuantity((prev) => {
      if (prev + 1 <= maxQuantity) {
        return prev + 1;
      }
      return prev;
    });
  };

  const decQuantity = () => {
    setQuantity((prev) => {
      if (prev > 1) {
        return prev - 1;
      }
      return prev;
    });
  };

  const handeAddToCart = () => {
    addToCart(product, quantity);
    setQuantity(1);
  };

  //show quantity only if is under the threshold
  let availability = message ?? '';
  if (!availability && levels) {
    availability = getFormattedAvailabilityText(
      levels,
      locale,
      daysPromptDeliveryLimit,
      availabilityShowThreshold,
      1 //qt
    );
  }

  if (!maxQuantity) {
    return null;
  }

  const setMaximumItemForCounter = (order: Order, product: ProductAndBundleDataType) => {
    const quantityCart = getCartQuantityforProduct(order, product.skuCode);

    if (quantityCart) {
      setMaxQty(maxQuantity - quantityCart);
    }
  };

  const isPossibileAddToCart = checkIfPossibleAddToCart(state.order, maxQuantity, product.skuCode);

  return (
    <div className="product-hero__component">
      <div className="product-hero__action__wrap">
        {availability && (
          <div className="product-hero__availability__wrap">
            <span className="product-hero__availability__label">{t(locale, 'availability')}</span>
            <span className="product-hero__availability">{availability}</span>
          </div>
        )}
        <div className="product-hero__quantity-selector">
          <button type="button" className="button" onClick={decQuantity} disabled={quantity <= 1}>
            {' - '}
          </button>
          <input
            name="quantity"
            type="number"
            min="1"
            max={maxQty}
            step="1"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value))}
          />
          <button
            type="button"
            className="button"
            disabled={maxQuantity && quantity >= maxQty}
            onClick={incQuantity}
          >
            {' + '}
          </button>
        </div>

        <div className="product-hero__action">
          <button
            type="button"
            onClick={handeAddToCart}
            className="button--primary"
            disabled={quantity > maxQty || !isPossibileAddToCart}
          >
            {t(locale, 'add_to_cart')}
          </button>
        </div>
      </div>
      {(quantity > maxQty || !isPossibileAddToCart) && (
        <div className="product-hero__quantity">
          {tt(locale, 'maximum_elements_added', {
            n: maxQuantity,
            p: t(
              locale,
              product.maxQuantity === 1 ? 'single_product_basepath' : 'product_basepath'
            ),
          })}
        </div>
      )}
    </div>
  );
};
